import React, { Ref, useRef, useEffect, useCallback } from 'react';
import { Typography, Link, Chip } from '@mui/material';
import { DayCardMenu, DayCardContent } from '.';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import useDragDrop, { DragIndices } from 'state/hooks/ui/use-dragdrop';
import { MoveLessonModal } from 'components/@common/Modals/MoveLessonModal';
import { BaseModalTitle } from 'components/@common/Modals/BaseModalTitle';
import { translate } from 'translations/translator';
import { LessonPlan, Assignment } from 'state/modules/entity/types';
import { getEpochTime, isEqualDate } from 'util/date-util';
import { useSelector, useDispatch, batch } from 'react-redux';
import { AppStoreState } from 'types';

import {
  swapLessonItems,
  groupTeachingOrderLessonIds,
  combineLessonItems,
  filterBumpLessonDates,
  calcWorkbookPreviewSize,
  moveLessonToEnd,
  hasStartedOrCompletedAttempts,
  getStartedAndCompletedAttempts,
  isReleasedToStudents,
  moveLessons,
  createEmptyDatesByOriginAndTarget,
  createEmptyDatesAfterSwap,
  getColRelativeToWeek
} from 'util/lesson-plan-util';
import {
  createCustomTeachingOrder,
  updateAndCombineTeachingOrder,
  updateTeachingOrderLessonsAndFetchLessonPlan,
  renameTeachingOrder,
  updateEmptyDates
} from 'state/modules/entity/thunks/teaching-order';
import { useAlert, AlertVariant, AlertStyleVariant } from 'state/providers/AlertProvider';
import { EditTeachingOrderModal } from 'components/@common/Modals/EditTeachingOrderModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen } from '@fortawesome/pro-regular-svg-icons';
import WorkbookPageModal from 'components/@common/Modals/WorkbookPageModal';
import { useNavigate, useParams } from 'react-router';
import { buildSearchQueryParams } from 'util/query-params-util';
import { UnassignLessonModal } from 'components/@common/Modals/UnassignLessonModal';
import { deleteUserAssignment, getAssignment } from 'state/modules/entity/thunks/assignment';
import { AssignmentEngineParams } from 'state/providers/LessonAssignmentProvider/types';
import { RemoveLessonModal } from 'components/@common/Modals/RemoveLessonModal';
import { EditAssignmentModal } from 'components/@common/Modals/EditAssignmentModal';
import { createEvent } from 'api';
import t from 'translations/variants/lessonPlanner';
import LessonAssignmentTranslationVariant from 'translations/variants/lessonAssignment';
import { getLicenseIsTrial } from 'state/modules/user/selectors/user-selectors';

const getTranslation = (key: t | string, options?: { [key: string]: string }) =>
  translate('lessonPlanner', key, options);

const assignmentTranslation = key => translate('lessonAssignment', key);
type Props = {
  lesson: LessonPlan & Assignment;
  lessonDate: string;
  classes: {
    card: string;
    titleRow: string;
    button: string;
    dayCardAssignment: string;
    cardBody?: string;
    alertActionBtn?: string;
    chip?: string;
  };
  styleOverride?: any;
  dragIndices: DragIndices | any;
  readonly: boolean;
};

export type SwapDates = {
  originDate: string;
  targetDate: string;
};

export const DayCardAssignmentContent: React.FC<Props> = ({
  lesson,
  classes,
  dragIndices,
  lessonDate,
  readonly,
  ...props
}: Props) => {
  const [, { openModal, openDraggableModal }] = useModal();
  const [, { openAlert }] = useAlert();
  const ref: Ref<any> = useRef(null);
  const dispatch = useDispatch() as any;
  const navigate = useNavigate();
  const params = useParams();
  const user = useSelector(({ user }: AppStoreState) => user);
  const isValidTrialUser: boolean = useSelector(getLicenseIsTrial);
  const currentLessonWeek = useSelector(
    ({ lessonPlanner }: AppStoreState) => lessonPlanner.currentlessonPlanWeek
  );
  const classrooms: any = useSelector(({ entity }: AppStoreState) => entity.classrooms);
  const isAssignmentView = lesson?.studentIds?.length ?? (-1 > 0 || lesson?.classroomId);
  const shouldDisable = user.isAuditor || isValidTrialUser || !classrooms?.length;
  /** use dragDropInputs when originIndices is undefined if move is triggered by menu */
  const dragDropInputs = {
    ref,
    draggable: shouldDisable ? false : !isAssignmentView,
    dragIndices: {
      col: getColRelativeToWeek(dragIndices?.col || 0, currentLessonWeek),
      item: dragIndices?.item || 0,
      itemId: dragIndices?.itemId || 0
    }
  };

  const [{ endDrag, ...dragStates }, { drag, drop }] = useDragDrop(dragDropInputs);
  const { lessons, custom }: any = useSelector(({ entity }: AppStoreState) => entity.lessonPlan);
  const lessonPlan: any = useSelector(({ entity }: AppStoreState) => entity.lessonPlan);
  const currentWorkbook: any = useSelector(({ app }: AppStoreState) => app.currentWorkbook);
  const productConfig = useSelector(({ entity }: AppStoreState) => entity.productConfig);

  const { bookCode, edition } = params as any;

  const getAssignmentRoute = useCallback(
    dateStr => {
      const [{ productCurriculumId }] = currentWorkbook.products;
      return `/lesson-assignment/${bookCode}/${edition}/${productCurriculumId}/${getEpochTime(
        dateStr
      )}`;
    },
    [bookCode, edition, currentWorkbook]
  );

  const handleRenameTeachingOrder = useCallback(
    async ({ id, custom }, newName) => {
      await dispatch(renameTeachingOrder(id, custom, newName));
      openAlert({
        body: getTranslation(t.teachingOrderNameChangedSuccessfully),
        variant: AlertVariant.SUCCESS,
        styleVariant: AlertStyleVariant.FILLED
      });
    },
    [dispatch, openAlert]
  );

  const openEditTeachingOrderModal = useCallback(
    customTechingOrder => () => {
      openModal({
        body: (
          <EditTeachingOrderModal
            handleRenameTeachingOrder={newName =>
              handleRenameTeachingOrder(customTechingOrder, newName)
            }
          />
        ),
        Title: () => <BaseModalTitle text={getTranslation(t.renameTeachingOrder)} />
      });
    },
    [handleRenameTeachingOrder, openModal]
  );

  const openSuccessAlert = useCallback(
    customTechingOrder => {
      openAlert({
        title: '',
        body: (
          <span>
            {getTranslation('customTeachingOrderCreated1')} {customTechingOrder.name}.
            {getTranslation('customTeachingOrderCreated2')}
            <Link
              className={classes.alertActionBtn}
              onClick={openEditTeachingOrderModal(customTechingOrder)}
            >
              {getTranslation('clickingHere')}
            </Link>
          </span>
        ),
        variant: AlertVariant.SUCCESS,
        styleVariant: AlertStyleVariant.FILLED
      });
    },
    [openAlert, classes.alertActionBtn, openEditTeachingOrderModal]
  );

  const createCustomTeachingOrderFlow = useCallback(
    async (teachingOrderLessonIds, emptyDates?) => {
      const {
        data: { customTeachingOrder }
      } = await dispatch(createCustomTeachingOrder(teachingOrderLessonIds, emptyDates));

      createEvent('teachingOrderCreated', {
        ...customTeachingOrder,
        ...(emptyDates && { emptyDates: emptyDates.map(({ date }) => date) })
      });

      openSuccessAlert(customTeachingOrder);
    },
    [openSuccessAlert, dispatch]
  );

  const handleMoveAndSwap = useCallback(
    (originIndices, targetIndices) =>
      async ({ targetDate }: SwapDates) => {
        const swappedLessonItems: any = swapLessonItems(lessons, {
          originIndices: originIndices || dragDropInputs.dragIndices,
          targetIndices,
          targetDate
        });
        const originCol = originIndices?.col || dragDropInputs.dragIndices.col;
        const targetCol = targetIndices?.col || dragDropInputs.dragIndices.col;
        const didSwapToEmptySlots =
          !swappedLessonItems[originCol]?.lessons.length || !lessons[targetCol]?.lessons?.length;

        const teachingOrderLessonIds = groupTeachingOrderLessonIds(swappedLessonItems);

        const emptyDates = createEmptyDatesAfterSwap(swappedLessonItems, {
          originIndices: originIndices || dragDropInputs.dragIndices,
          targetDate
        });

        if (!custom) {
          const includeEmptyDatesOrNot = !swappedLessonItems[originCol]?.lessons?.length
            ? emptyDates
            : undefined;

          createCustomTeachingOrderFlow(teachingOrderLessonIds, includeEmptyDatesOrNot);
          return;
        }

        batch(async () => {
          if (didSwapToEmptySlots) {
            await dispatch(updateEmptyDates(emptyDates));
          }

          await dispatch(updateTeachingOrderLessonsAndFetchLessonPlan(teachingOrderLessonIds));
          await openAlert({
            body: getTranslation(t.teachingOrderChanged),
            variant: AlertVariant.SUCCESS,
            styleVariant: AlertStyleVariant.FILLED
          });
        });
      },
    [
      dispatch,
      lessons,
      createCustomTeachingOrderFlow,
      custom,
      openAlert,
      dragDropInputs.dragIndices
    ]
  );

  const handleMoveAndCombine = useCallback(
    (originIndices, targetIndices) =>
      async ({ targetDate }: SwapDates) => {
        const { dragIndices } = dragDropInputs;
        const indices = originIndices || dragIndices;

        const emptyDates = createEmptyDatesByOriginAndTarget(lessons, {
          originIndices: indices,
          targetDate
        });

        const targetIndex: number = lessons.findIndex(({ lessonDate }) =>
          isEqualDate(lessonDate, targetDate)
        );

        if (!lessons[targetIndices?.col ?? targetIndex].lessons) {
          const movedLessons = moveLessons(lessons, {
            originIndices: indices,
            targetDate
          });
          const emptyTargetLessonIds = groupTeachingOrderLessonIds(movedLessons);

          if (!custom) {
            await createCustomTeachingOrderFlow(emptyTargetLessonIds, emptyDates);
            return;
          }

          batch(async () => {
            await dispatch(
              updateTeachingOrderLessonsAndFetchLessonPlan(emptyTargetLessonIds, false)
            );
            await dispatch(updateEmptyDates(emptyDates));
          });
          return;
        }

        const combineLessonItem: any = combineLessonItems(lessons, {
          originIndices: indices,
          targetIndices,
          targetDate
        });

        const teachingOrderLessonIds = groupTeachingOrderLessonIds(combineLessonItem);

        if (custom) {
          await dispatch(
            updateAndCombineTeachingOrder(lessonPlan.id, teachingOrderLessonIds, emptyDates)
          );
          return;
        }

        createCustomTeachingOrderFlow(teachingOrderLessonIds, emptyDates);
      },
    [dispatch, createCustomTeachingOrderFlow, custom, lessons, lessonPlan.id, dragDropInputs]
  );

  const handleBumpLesson = useCallback(
    originIndices =>
      async ({ targetDate }: SwapDates) => {
        const emptyDates = filterBumpLessonDates(lessons, {
          originIndices: originIndices || dragDropInputs.dragIndices,
          targetDate
        });

        await dispatch(updateEmptyDates(emptyDates));

        openAlert({
          body: getTranslation(t.teachingOrderChanged),
          variant: AlertVariant.SUCCESS,
          styleVariant: AlertStyleVariant.FILLED
        });
      },
    [dispatch, dragDropInputs.dragIndices, lessons, openAlert]
  );

  const openMoveLessonModal = useCallback(
    ({ lessonDate, originIndices, targetIndices }) => {
      const getMoveActions = () => ({
        handleMoveAndSwap: handleMoveAndSwap(originIndices, targetIndices),
        handleMoveAndCombine: handleMoveAndCombine(originIndices, targetIndices),
        handleBumpLesson: handleBumpLesson(originIndices)
      });
      const disableMoveAndSwap = !lessons[targetIndices?.col || 0].lessons;

      openModal({
        body: (
          <MoveLessonModal
            startDate={lessonPlan.lessons?.find(x => x.lessonDate !== null).lessonDate}
            defaultDate={new Date(lessonDate)}
            disabledMove={disableMoveAndSwap}
            didDrag={originIndices && Object.keys(originIndices).length > 0}
            nonMoveToDates={productConfig.skipDates}
            emptyDates={productConfig.emptyDates}
            {...getMoveActions()}
          />
        ),
        Title: () => <BaseModalTitle text={getTranslation(t.moveLessonTo)} />
      });
    },
    [
      openModal,
      handleMoveAndSwap,
      handleBumpLesson,
      handleMoveAndCombine,
      lessons,
      productConfig,
      lessonPlan.lessons
    ]
  );

  const openTeachersGuidePreviewModal = useCallback(
    pageNumber => {
      const tg = currentWorkbook.teachersGuides[0];
      const { width, height } = calcWorkbookPreviewSize(window.innerHeight, true);
      const modalStyles = { backgroundColor: '#EEEEEE', height, padding: 20 };

      openDraggableModal({
        body: <WorkbookPageModal workbook={tg} currentPage={pageNumber} width={width} />,
        titleText: `${tg.title} ${getTranslation(t.teachersGuide, { pageNumber })}`,
        getContainerStyles: defaultStyles => ({ ...defaultStyles, ...modalStyles }),
        options: { closeOnBackdropClick: true }
      });
    },
    [openDraggableModal, currentWorkbook]
  );

  useEffect(() => {
    if (dragStates.didDrop && Object.keys(endDrag).length && endDrag.canSwap) {
      const {
        items: { targetIndices }
      } = endDrag;

      openMoveLessonModal({
        lessonDate: lessons[targetIndices.col].lessonDate || lessonDate,
        ...endDrag.items
      });
    }
  }, [dragStates.didDrop, endDrag, openMoveLessonModal, lessons, lessonDate]);

  useEffect(() => {
    drag(drop(ref));
  }, [drag, drop, ref]);

  const handleViewAssignments = () => {
    const { bookCode, edition } = params as any;
    const epochTime = getEpochTime(lesson.dateDue);

    navigate({
      pathname: `/books/${bookCode}/${edition}/lesson-planner/view`,
      search: buildSearchQueryParams({ epochTime })
    });
  };

  const getAttempts = useCallback(
    async (assignmentId: number) => {
      const {
        data: { assignmentDetail }
      } = await dispatch(getAssignment(assignmentId, { includeAttempts: true }));

      return assignmentDetail.attempts;
    },
    [dispatch]
  );

  const handleOnUnassign = useCallback(
    async (
      { id: assignmentId, classroomId, studentIds, teachingOrderLessonId, dateAssigned, dateDue },
      { started, completed },
      params
    ) => {
      await dispatch(deleteUserAssignment(assignmentId, params));

      createEvent('assignmentDeleted', {
        assignmentId,
        classroomId,
        studentIds,
        teachingOrderLessonId,
        dateAssigned,
        dateDue,
        ...((started || completed) && { attempts: { started, completed } }),
        ...params
      });

      openAlert({
        body: assignmentTranslation(LessonAssignmentTranslationVariant.unassignSuccessful),
        variant: AlertVariant.SUCCESS
      });
    },
    [dispatch, openAlert]
  );

  const handleUnassign = assignment => async () => {
    const attempts = await getAttempts(assignment.id);
    const { started, completed } = getStartedAndCompletedAttempts(attempts);
    const assignmentReleased = isReleasedToStudents(assignment);
    const params = {
      ...((assignmentReleased || started || completed) && { force: true })
    };

    openModal({
      Title: () => <BaseModalTitle text={assignmentTranslation('unassignLesson')} />,
      body: (
        <UnassignLessonModal
          handleUnassign={() => handleOnUnassign(assignment, { started, completed }, params)}
          completed={completed}
          started={started}
        />
      )
    });
  };

  const handleEditAssignments = useCallback(
    assignment => async () => {
      const attempts = await getAttempts(assignment.id);

      if (hasStartedOrCompletedAttempts(attempts)) {
        const startedAndCompleted = getStartedAndCompletedAttempts(attempts);
        openModal({
          Title: () => <BaseModalTitle text={getTranslation(t.editAssignment)} />,
          body: (
            <EditAssignmentModal
              onSubmit={() => handleOnUnassign(assignment, startedAndCompleted, { force: true })}
            />
          )
        });
        return;
      }

      navigate({
        pathname: getAssignmentRoute(assignment.dateDue),
        search: buildSearchQueryParams({
          assignment: AssignmentEngineParams.EDIT,
          assignmentId: assignment.id
        })
      });
    },
    [navigate, handleOnUnassign, openModal, getAttempts, getAssignmentRoute]
  );

  const handleAssignLesson = useCallback(
    lesson => () => {
      navigate({
        pathname: getAssignmentRoute(lesson.lessonDate),
        search: buildSearchQueryParams({
          assignment: AssignmentEngineParams.SINGLE,
          id: lesson.teachingOrderLessonId
        })
      });
    },
    [navigate, getAssignmentRoute]
  );

  const handleRemoveLesson = useCallback(
    (lesson, cardIndices: DragIndices) => () => {
      const handleSubmit = async () => {
        const rearrangedItems = moveLessonToEnd(lessons, cardIndices);
        const teachingOrderLessonIds = groupTeachingOrderLessonIds(rearrangedItems);

        if (!custom) {
          createCustomTeachingOrderFlow(teachingOrderLessonIds);
          return;
        }

        await dispatch(updateTeachingOrderLessonsAndFetchLessonPlan(teachingOrderLessonIds));
      };

      openModal({
        Title: () => <BaseModalTitle text={getTranslation(t.removeLesson)} />,
        body: <RemoveLessonModal lessonName={lesson.title} handleOnRemove={handleSubmit} />
      });
    },
    [openModal, createCustomTeachingOrderFlow, custom, dispatch, lessons]
  );

  const DayCardContentHeader = ({
    lessonDate,
    assigned,
    studentsAssigned,
    isAssignmentsView,
    lesson,
    cardIndices
  }) => (
    <div className={classes.card} style={props.styleOverride}>
      <div className={classes.titleRow}>
        <Typography style={{ fontWeight: 600 }} variant="subtitle1">
          {lesson.title}
        </Typography>
        <DayCardMenu
          buttonClass={classes.button}
          openMoveLessonModal={() => openMoveLessonModal({ lessonDate } as any)}
          isAssignmentsView={isAssignmentsView}
          handleViewAssignments={handleViewAssignments}
          handleUnassign={handleUnassign(lesson)}
          handleEditAssignments={handleEditAssignments(lesson)}
          handleRemoveLesson={handleRemoveLesson(lesson, cardIndices)}
          handleAssignLesson={handleAssignLesson({ ...lesson, lessonDate })}
        />
      </div>
      {lesson.page?.pageNumber && (
        <span
          style={{ cursor: 'pointer', fontSize: '14px', color: '#2878C1' }}
          onClick={() => openTeachersGuidePreviewModal(lesson.page.pageNumber)}
        >
          <FontAwesomeIcon size="1x" style={{ marginRight: 7, marginTop: 5 }} icon={faBookOpen} />
          p. {lesson.page.pageNumber}
        </span>
      )}
      {studentsAssigned ? (
        <Chip label={`${studentsAssigned} Students`} className={classes.chip} />
      ) : (
        assigned && <Chip label={getTranslation('assignment')} className={classes.chip} />
      )}
    </div>
  );

  const maybeDraggableProps: any = dragIndices
    ? {
        ref: ref,
        style: {
          border:
            dragStates.canDrop && dragStates.isOver ? '2px solid #2878C1' : '1px solid #A6A6A6',
          opacity: dragStates.isDragging ? 0 : 1,
          fontSize: 25,
          fontWeight: 'bold'
          //cursor: !isAssignmentView || readonly ? '' : 'move'
        }
      }
    : undefined;

  return (
    <div {...maybeDraggableProps} className={classes.dayCardAssignment}>
      <DayCardContentHeader
        lessonDate={lessonDate}
        assigned={lesson?.assigned === true}
        studentsAssigned={lesson?.studentIds?.length}
        isAssignmentsView={isAssignmentView}
        lesson={lesson}
        cardIndices={dragDropInputs.dragIndices}
      />
      <DayCardContent
        className={`${classes.card} ${classes.cardBody ? classes.cardBody : ''}`}
        lesson={lesson}
      />
    </div>
  );
};
